const DB_NAME = 'digital-receipts-offline';
const DB_VERSION = 2;
let offlineScope = 'anonymous';

export function setOfflineScope(scope) {
  offlineScope = scope || 'anonymous';
}

function scopedKey(id) {
  return `${offlineScope}:${id}`;
}

function addScope(item, key = item.id) {
  return { ...item, __cache_key: scopedKey(key) };
}

function removeScope(item) {
  if (!item) return item;
  const { __cache_key, ...rest } = item;
  return rest;
}

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (event.oldVersion < 2) {
        ['farmers', 'products', 'receipts', 'pendingReceipts', 'productUnits'].forEach((store) => {
          if (db.objectStoreNames.contains(store)) db.deleteObjectStore(store);
        });
      }
      ['farmers', 'products', 'receipts', 'pendingReceipts'].forEach((store) => {
        if (!db.objectStoreNames.contains(store)) db.createObjectStore(store, { keyPath: '__cache_key' });
      });
      if (!db.objectStoreNames.contains('productUnits')) db.createObjectStore('productUnits', { keyPath: '__cache_key' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function tx(storeName, mode, callback) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    const result = callback(store);
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
  }).finally(() => db.close());
}

function req(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function putMany(storeName, items) {
  if (!items?.length) return;
  await tx(storeName, 'readwrite', (store) => items.forEach((item) => store.put(addScope(item))));
}

export async function putOne(storeName, item) {
  await tx(storeName, 'readwrite', (store) => store.put(addScope(item)));
}

export async function getAll(storeName) {
  const items = await tx(storeName, 'readonly', (store) => req(store.getAll()));
  return items.filter((item) => item.__cache_key?.startsWith(`${offlineScope}:`)).map(removeScope);
}

export async function removeOne(storeName, id) {
  await tx(storeName, 'readwrite', (store) => store.delete(scopedKey(id)));
}

export async function cacheProductUnits(productId, units) {
  await tx('productUnits', 'readwrite', (store) => store.put(addScope({ product_id: productId, units }, productId)));
}

export async function getCachedProductUnits(productId) {
  const entry = await tx('productUnits', 'readonly', (store) => req(store.get(scopedKey(productId))));
  return entry?.units || [];
}

export async function addPendingReceipt(pending) {
  await putOne('pendingReceipts', pending);
}

export async function markPendingReceipt(id, changes) {
  const existing = await tx('pendingReceipts', 'readonly', (store) => req(store.get(scopedKey(id))));
  if (!existing) return;
  await putOne('pendingReceipts', removeScope({ ...existing, ...changes }));
}

export async function removePendingReceipt(id) {
  await removeOne('pendingReceipts', id);
}

export async function getPendingReceipts() {
  return getAll('pendingReceipts');
}

export async function cacheReceipt(receipt) {
  await putOne('receipts', receipt);
}

export async function cacheReceipts(receipts) {
  await putMany('receipts', receipts);
}

export async function getCachedReceipts() {
  return getAll('receipts');
}

export function searchCached(items, query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return items.filter((item) => {
    return [item.name, item.username, item.reference_number, item.registration_number].filter(Boolean).some((value) => {
      return String(value).toLowerCase().includes(q);
    });
  }).slice(0, 10);
}
